import { useEffect, useState, type FormEvent } from 'react';
import type { Contact, Customer } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { useCreateContact } from '../contacts/api.js';
import { useCreateCustomer } from '../customers/api.js';

function errorMessage(error: unknown, fallback: string): string | null {
  if (error instanceof ApiError) {
    return error.message;
  }
  return error ? fallback : null;
}

interface QuickCreateCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (customer: Customer) => void;
}

/** Minimal "create a company" prompt so a new customer can be added without leaving the opportunity. */
export function QuickCreateCustomerModal({
  isOpen,
  onClose,
  onCreated,
}: QuickCreateCustomerModalProps): JSX.Element {
  const [name, setName] = useState('');
  const mutation = useCreateCustomer();
  const resetMutation = mutation.reset;

  useEffect(() => {
    if (isOpen) {
      setName('');
      resetMutation();
    }
  }, [isOpen, resetMutation]);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    mutation.mutate(
      { name: trimmed },
      {
        onSuccess: (customer) => {
          onCreated(customer);
          onClose();
        },
      },
    );
  };

  const error = errorMessage(mutation.error, 'Failed to create company.');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New company"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="quick-create-customer-form" isLoading={mutation.isPending}>
            Create company
          </Button>
        </>
      }
    >
      <form id="quick-create-customer-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error ? (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
        ) : null}
        <TextField
          label="Company name"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <p className="text-xs text-slate-500">
          You can add the rest of the company details later from the Customers page.
        </p>
      </form>
    </Modal>
  );
}

interface QuickCreateContactModalProps {
  isOpen: boolean;
  /** Company the new contact belongs to. */
  customerId: string;
  /** Optional dialog title override (e.g. "New NetSuite contact"). */
  title?: string;
  onClose: () => void;
  onCreated: (contact: Contact) => void;
}

interface ContactFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
}

const EMPTY_CONTACT: ContactFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  title: '',
};

/** Minimal "create a contact" prompt scoped to a company. */
export function QuickCreateContactModal({
  isOpen,
  customerId,
  title = 'New contact',
  onClose,
  onCreated,
}: QuickCreateContactModalProps): JSX.Element {
  const [form, setForm] = useState<ContactFormState>(EMPTY_CONTACT);
  const mutation = useCreateContact();
  const resetMutation = mutation.reset;

  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_CONTACT);
      resetMutation();
    }
  }, [isOpen, resetMutation]);

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim();
    if (!firstName || !lastName || !email || !customerId) {
      return;
    }
    mutation.mutate(
      {
        customerId,
        firstName,
        lastName,
        email,
        phone: form.phone.trim() ? form.phone.trim() : null,
        title: form.title.trim() ? form.title.trim() : null,
      },
      {
        onSuccess: (contact) => {
          onCreated(contact);
          onClose();
        },
      },
    );
  };

  const error = errorMessage(mutation.error, 'Failed to create contact.');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="quick-create-contact-form" isLoading={mutation.isPending}>
            Create contact
          </Button>
        </>
      }
    >
      <form id="quick-create-contact-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error ? (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
        ) : null}
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="First name"
            required
            autoFocus
            value={form.firstName}
            onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
          />
          <TextField
            label="Last name"
            required
            value={form.lastName}
            onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
          />
        </div>
        <TextField
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
          />
          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          />
        </div>
      </form>
    </Modal>
  );
}
